import { useState, useRef } from 'react';
import RakenneosaSelect from './RakenneosaSelect';

/**
 * Shared rakenneosa tab bar used by all julkisivu measurement blocks.
 * Props:
 *   kohteet           – array of {id, nimi}
 *   activeKohdeId     – currently active kohde id
 *   tableStartNumber  – first table number (null → 1-based index)
 *   onActivate(id)    – select a kohde
 *   onCreate(name, withNayte) – async: create kohde; withNayte=true if user also wants a poranäyte
 *   onRename(id,name) – async: rename kohde
 *   onDelete(id)      – async: delete kohde (parent updates kohteet state)
 *   hideTabs          – hide individual tab chips (keep the add button visible)
 *   withNaytePrompt   – show "Luo myös poranäyte?" dialog after name is confirmed
 */
export default function KohdeTabBar({ kohteet, activeKohdeId, tableStartNumber, onActivate, onCreate, onAdd, addLabel, onRename, onDelete, hideTabs, displayNimi, withNaytePrompt, withTunnusInput }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingAdd, setPendingAdd] = useState(false);
  const [pendingAddValue, setPendingAddValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [pendingNayteConfirm, setPendingNayteConfirm] = useState(null); // string name
  const [pendingNayteTunnus, setPendingNayteTunnus] = useState('');
  const [pendingTunnusConfirm, setPendingTunnusConfirm] = useState(null); // string name
  const [pendingTunnusValue, setPendingTunnusValue] = useState('');
  const renameCommittedRef = useRef(false);
  const addCommittedRef = useRef(false);

  const startRename = (id, value) => {
    renameCommittedRef.current = false;
    setRenamingId(id);
    setRenameValue(value);
  };

  const handleAdd = () => {
    if (creating || pendingAdd || !onCreate) return;
    addCommittedRef.current = false;
    setPendingAddValue('');
    setPendingAdd(true);
  };

  const commitPendingAdd = async (overrideValue) => {
    if (addCommittedRef.current) return;
    addCommittedRef.current = true;
    const name = (overrideValue ?? pendingAddValue).trim();
    setPendingAdd(false);
    if (!name) return;
    if (withTunnusInput) {
      setPendingTunnusValue('');
      setPendingTunnusConfirm(name);
      return;
    }
    if (withNaytePrompt) {
      setPendingNayteConfirm(name);
      return;
    }
    setCreating(true);
    try {
      await onCreate(name, false);
    } catch (err) {
      console.error('[KohdeTabBar] onCreate failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleNayteChoice = async (withNayte, tunnus = '') => {
    const name = pendingNayteConfirm;
    setPendingNayteConfirm(null);
    setPendingNayteTunnus('');
    if (!name) return;
    setCreating(true);
    try {
      await onCreate(name, withNayte, tunnus);
    } catch (err) {
      console.error('[KohdeTabBar] onCreate failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleTunnusCreate = async () => {
    const name = pendingTunnusConfirm;
    const tunnus = pendingTunnusValue.trim();
    setPendingTunnusConfirm(null);
    setPendingTunnusValue('');
    if (!name) return;
    setCreating(true);
    try {
      await onCreate(name, false, tunnus);
    } catch (err) {
      console.error('[KohdeTabBar] onCreate failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const commitRename = async (overrideValue) => {
    if (renameCommittedRef.current) return;
    renameCommittedRef.current = true;
    const trimmed = (overrideValue ?? renameValue).trim();
    const id = renamingId;
    setRenamingId(null);
    if (trimmed && id && onRename) {
      try { await onRename(id, trimmed); } catch (err) { console.error('[KohdeTabBar] onRename failed:', err); }
    }
  };

  const handleConfirmDelete = async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (id && onDelete) {
      try { await onDelete(id); } catch (err) { console.error('[KohdeTabBar] onDelete failed:', err); }
    }
  };

  const list = kohteet ?? [];

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Rakenneosa</div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        {!hideTabs && list.map((k) => {
          const isActive = k.id === activeKohdeId;
          return (
            <div key={k.id}
              onClick={() => renamingId !== k.id && onActivate?.(k.id)}
              style={{
                padding: '2px 8px', border: '1px solid', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
                borderColor: isActive ? '#198754' : '#dee2e6',
                background: isActive ? '#f0fdf4' : '#fff',
                color: isActive ? '#15803d' : '#6c757d',
                fontWeight: isActive ? 600 : 400,
                fontSize: '0.78rem',
                display: 'flex', alignItems: 'center', gap: 5,
                userSelect: 'none',
              }}>
              {renamingId === k.id ? (
                <div onClick={e => e.stopPropagation()}>
                  <RakenneosaSelect
                    autoFocus
                    value={renameValue}
                    onChange={setRenameValue}
                    onCommit={v => commitRename(v)}
                    onBlur={() => commitRename()}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    placeholder="Valitse rakenneosa…"
                    style={{
                      fontSize: '0.78rem', fontWeight: 600, border: 'none',
                      borderBottom: '1px solid #198754', outline: 'none',
                      background: 'transparent', padding: '0 2px',
                    }}
                  />
                </div>
              ) : (
                <span onDoubleClick={e => { e.stopPropagation(); startRename(k.id, k.nimi); }}>
                  {displayNimi ? displayNimi(k) : k.nimi}
                </span>
              )}
              {renamingId !== k.id && (
                <span
                  role="button"
                  onClick={e => { e.stopPropagation(); setPendingDeleteId(k.id); }}
                  style={{ color: '#adb5bd', fontSize: '1rem', lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.color='#dc3545'}
                  onMouseLeave={e => e.currentTarget.style.color='#adb5bd'}
                  title="Poista rakenneosa"
                >×</span>
              )}
            </div>
          );
        })}

        {(onCreate || onAdd) && (onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{ padding: '2px 8px', border: '1px dashed #0d6efd', borderRadius: 3, background: '#fff', color: '#0d6efd', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >{addLabel ?? '+ Lisää rakenneosa'}</button>
        ) : pendingAdd ? (
          <div style={{ padding: '2px 8px', border: '1px solid #0d6efd', borderRadius: 3, background: '#fff', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <RakenneosaSelect
              autoFocus
              value={pendingAddValue}
              onChange={setPendingAddValue}
              onCommit={v => commitPendingAdd(v)}
              onBlur={() => commitPendingAdd()}
              onKeyDown={e => {
                if (e.key === 'Enter') commitPendingAdd();
                if (e.key === 'Escape') { setPendingAdd(false); }
              }}
              placeholder="Valitse rakenneosa…"
              style={{
                fontSize: '0.78rem', fontWeight: 600, border: 'none',
                outline: 'none', background: 'transparent', padding: '0 2px',
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAdd}
            disabled={creating}
            style={{ padding: '2px 8px', border: '1px dashed #0d6efd', borderRadius: 3, background: '#fff', color: '#0d6efd', fontSize: '0.75rem', fontWeight: 600, cursor: creating ? 'wait' : 'pointer' }}
          >
            {creating
              ? <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} />
              : '+ Lisää rakenneosa'}
          </button>
        ))}
      </div>
      </div>

      {/* Nayte prompt dialog */}
      {pendingNayteConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) handleNayteChoice(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(380px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Luo poranäyte?</div>
            <div style={{ fontSize: '0.88rem', color: '#495057' }}>
              Haluatko luoda myös poranäytteen rakenneosalle <strong>{pendingNayteConfirm}</strong>?
            </div>
            <input
              autoFocus
              type="text"
              placeholder="Näytetunnus (esim. P1)"
              value={pendingNayteTunnus}
              onChange={e => setPendingNayteTunnus(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleNayteChoice(true, pendingNayteTunnus); }}
              style={{ border: '1px solid #ced4da', borderRadius: 4, padding: '6px 10px', fontSize: '0.9rem', outline: 'none', width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => handleNayteChoice(false)}>Ei, vain rakenneosa</button>
              <button className="btn btn-sm btn-success" onClick={() => handleNayteChoice(true, pendingNayteTunnus)}>Kyllä, luo poranäyte</button>
            </div>
          </div>
        </div>
      )}

      {/* Tunnus input dialog */}
      {pendingTunnusConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) handleTunnusCreate(); }}
        >
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(320px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Näytetunnus — <span style={{ fontWeight: 400, color: '#495057' }}>{pendingTunnusConfirm}</span></div>
            <input
              autoFocus
              type="text"
              placeholder="esim. P1"
              value={pendingTunnusValue}
              onChange={e => setPendingTunnusValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleTunnusCreate();
                if (e.key === 'Escape') { setPendingTunnusConfirm(null); setPendingTunnusValue(''); }
              }}
              style={{ border: '1px solid #ced4da', borderRadius: 4, padding: '6px 10px', fontSize: '0.9rem', outline: 'none', width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setPendingTunnusConfirm(null); setPendingTunnusValue(''); }}>Ohita</button>
              <button className="btn btn-sm btn-success" onClick={handleTunnusCreate}>Luo näyte</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      {pendingDeleteId && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteId(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista rakenneosa</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>
              Haluatko poistaa rakenneosan <strong>{list.find(k => k.id === pendingDeleteId)?.nimi}</strong>?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteId(null)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={handleConfirmDelete}>Poista</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
